import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  // Initialize theme: Priority 1. localStorage → 2. system preference → 3. light
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('theme');
    if (saved !== null) {
      return saved === 'dark';
    }
    
    // Detect system preference
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return true;
    }
    return false;
  });

  const toggleTheme = () => {
    setIsDark(prev => !prev);
  };

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleSystemThemeChange = (e) => {
      // Only auto-switch if user hasn't manually saved a preference
      if (!localStorage.getItem('theme')) {
        setIsDark(e.matches);
      }
    };
    
    // Modern browsers support addEventListener on mediaQueryList
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleSystemThemeChange);
      return () => mediaQuery.removeEventListener('change', handleSystemThemeChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleSystemThemeChange);
      return () => mediaQuery.removeListener(handleSystemThemeChange);
    }
  }, []);

  // Save theme preference (only if user manually toggled) and update body styles
  useEffect(() => {
    // Only save to localStorage if it was previously set (manual toggle)
    // This prevents auto-detection from being overwritten
    const hasManualPreference = localStorage.getItem('theme') !== null;
    if (hasManualPreference) {
      localStorage.setItem('theme', isDark ? 'dark' : 'light');
    }
    
    document.body.style.backgroundColor = isDark ? '#000' : '#fff';
    document.body.style.color = isDark ? '#fff' : '#000';
  }, [isDark]);

  const value = {
    isDark,
    toggleTheme
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};